import { useQuery } from "@rocicorp/zero/react";
import { isDraft } from "../../shared/connectors/isDraft";
import { personalUseBlocked } from "../../shared/personalUse";
import { queries } from "../../shared/queries";
import {
  isSupplierAvailableFromGrants,
  principalHasSupplierPermissionFromGrants,
} from "../../shared/rbac/crossResourceAvailability";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { useGroupMemberships } from "../hooks/useGroupMemberships";

type Props = {
  connectorId?: string;
  step?: string;
  spaceId?: string;
  chatOwnerId?: string;
};

export function ConnectedConnectorStatus(props: Props) {
  if (!props.connectorId) return <UnavailableStatus />;
  if (props.spaceId)
    return (
      <SpaceConnectorStatus
        connectorId={props.connectorId}
        spaceId={props.spaceId}
        step={props.step}
      />
    );
  if (props.chatOwnerId)
    return (
      <PersonalChatConnectorStatus
        connectorId={props.connectorId}
        chatOwnerId={props.chatOwnerId}
      />
    );
  return <UnavailableStatus />;
}

function UnavailableStatus() {
  return <ToolStatus icon="lock" text="Connector unavailable." />;
}

function ConnectedStatus({ name, step }: { name: string; step?: string }) {
  return (
    <ToolStatus
      icon="check-filled"
      iconColor="var(--green-600)"
      text={step ? `Connected ${name} to ${step}` : `Connected ${name}`}
    />
  );
}

function SpaceConnectorStatus({
  connectorId,
  spaceId,
  step,
}: {
  connectorId: string;
  spaceId: string;
  step?: string;
}) {
  const [connector, connectorResult] = useQuery(
    queries.loadConnectorDetails({ connectedAppId: connectorId })
  );
  const [space, spaceResult] = useQuery(queries.navSpace({ spaceId }));
  const [spaceGrants, grantsResult] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const { userGroupIds: memberships, loaded: membershipsLoaded } =
    useGroupMemberships();
  if (
    !membershipsLoaded ||
    [connectorResult, spaceResult, grantsResult].some(
      (result) => result.type !== "complete"
    )
  )
    return null;

  const available =
    connector &&
    space &&
    !isDraft(connector) &&
    !personalUseBlocked(connector, { kind: "space", mode: space.mode }) &&
    isSupplierAvailableFromGrants(
      "space_uses_connected_apps",
      spaceGrants,
      connector.roleGrantsAsScope,
      memberships
    );

  if (!available) return <UnavailableStatus />;
  return <ConnectedStatus name={connector.name} step={step} />;
}

function PersonalChatConnectorStatus({
  connectorId,
  chatOwnerId,
}: {
  connectorId: string;
  chatOwnerId: string;
}) {
  const [connector, connectorResult] = useQuery(
    queries.loadConnectorDetails({ connectedAppId: connectorId })
  );
  const { userGroupIds: memberships, loaded: membershipsLoaded } =
    useGroupMemberships();
  if (!membershipsLoaded || connectorResult.type !== "complete") return null;

  const available =
    connector &&
    !isDraft(connector) &&
    !personalUseBlocked(connector, { kind: "personal_chat" }) &&
    principalHasSupplierPermissionFromGrants(
      chatOwnerId,
      memberships.get(chatOwnerId) ?? [],
      connector.roleGrantsAsScope,
      "connected_app:use"
    );

  if (!available) return <UnavailableStatus />;
  return <ConnectedStatus name={connector.name} />;
}
